import { useCallback, useState } from "react";
import { useRouter } from "next/router";
import { searchActivity as searchActivityApi } from "@/services/api/activities";
import {
  buildActivitySearchHref,
  buildSearchListHref,
  createSearchDraft,
  findSearchMatch,
  getSearchPrefix,
  SEARCH_MIN_CHARS,
} from "@/utils/activitySearch";
import { withRegionPath } from "@/utils/session";
import { beginListingSearch } from "@/utils/listingFilterSession";

const EMPTY_DRAFT = {
  label: "",
  selection: null,
};

export default function useActivityEditSearch({ urlParams, pageTitle = "" }) {
  const router = useRouter();
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [locationOpen, setLocationOpen] = useState(false);

  const initDraft = useCallback(async () => {
    const prefix = getSearchPrefix(urlParams, pageTitle);

    if (!prefix) {
      setDraft(EMPTY_DRAFT);
      return;
    }

    if (prefix.trim().length < SEARCH_MIN_CHARS) {
      setDraft({ ...EMPTY_DRAFT, label: prefix });
      return;
    }

    try {
      const response = await searchActivityApi(prefix.trim());
      const match = findSearchMatch(response, { prefix, urlParams });
      setDraft(match ? createSearchDraft(match) : { ...EMPTY_DRAFT, label: prefix });
    } catch {
      setDraft({ ...EMPTY_DRAFT, label: prefix });
    }
  }, [urlParams, pageTitle]);

  const openLocationSearch = useCallback(() => {
    setLocationOpen(true);
  }, []);

  const closeLocationSearch = useCallback(() => {
    setLocationOpen(false);
  }, []);

  const selectLocation = useCallback(
    (item) => {
      if (item?.kind === "activity") {
        setLocationOpen(false);
        beginListingSearch("activity");
        router.push(withRegionPath(buildActivitySearchHref(item)));
        return;
      }

      setDraft(createSearchDraft(item));
      setLocationOpen(false);
    },
    [router]
  );

  const setDestinationQuery = useCallback((label) => {
    setDraft((prev) => ({
      ...prev,
      label,
      selection: null,
    }));
  }, []);

  const applySearch = useCallback(() => {
    const href = draft.selection
      ? buildActivitySearchHref(draft.selection)
      : buildSearchListHref({ label: draft.label });

    beginListingSearch("activity");
    router.push(withRegionPath(href));
  }, [draft, router]);

  return {
    draft,
    locationOpen,
    initDraft,
    openLocationSearch,
    closeLocationSearch,
    selectLocation,
    setDestinationQuery,
    applySearch,
  };
}
